import { z } from "zod"
import { nanoid } from "nanoid"
import fetch from "isomorphic-fetch"

export const createComponentId = () => `__sw-${nanoid()}`

export const TemplateURLPartsSchema = z.object({
  host: z.string(),
  // Is this deployed from paywall-ui or paywall-next?
  paywallUIVariant: z.union([z.literal("base"), z.literal("legacy")]),
  // Is this a release or a review?
  reviewRelease: z.union([z.literal("release"), z.literal("review")]),
  // What version is this?
  version: z.string(),
  // What base is this?
  base: z.union([z.literal("base1"), z.literal("base2"), z.literal("base3")]),
  // What is the unique key for this template json?
  key: z.string(),
  // Extra query params which we propagate to the iframe
  queryParams: z.record(z.string()),
})

// http://builder-templates.superwall.localhost/base-release/latest/base2/template.html?key=__sw-KO8axnzkWLnBYyen-_bDo&debug=true
export type TemplateURLParts = z.infer<typeof TemplateURLPartsSchema>

export class TemplateURLHelper {
  public parts: TemplateURLParts | undefined
  private _originalURL: string
  public get url(): string {
    return this.parts ? this.composeURL(this.parts) : this._originalURL
  }

  constructor(partsOrURL: TemplateURLParts | string) {
    if (typeof partsOrURL === "string") {
      try {
        this.parts = this.parseURL(partsOrURL)
      } catch (e) {
        // If we can't parse the url, we'll just use the original url
      }
      // If the parsing failed, we'll just use the original url
      if (!this.parts) {
        this._originalURL = partsOrURL
        return
      }
    } else {
      this.parts = partsOrURL
    }
    this._originalURL = this.composeURL(this.parts)
  }

  get isBuilderTemplateURL(): boolean {
    if (!this.parts) {
      return false
    }

    // If the name contains builder-templates, it's a builder template
    return (
      this.parts.host.includes("builder-templates") || this.parts.host.includes("localhost:8998")
    )
  }

  private parseURL(url: string): TemplateURLParts | undefined {
    const parsedURL = new URL(url)
    const host = parsedURL.origin
    const pathParts = parsedURL.pathname.split("/").filter((part) => part)
    const paywallUIVariant = (pathParts[0] || "").startsWith("base") ? "base" : "legacy"
    const reviewRelease = (pathParts[0] || "").replace("base-", "")
    const version = pathParts[1] || ""
    const base = pathParts[2] || ""

    const searchParams = new URLSearchParams(parsedURL.search)
    const key = searchParams.get("key") || ""
    const queryParams: Record<string, string> = {}

    Array.from(searchParams.keys()).forEach((key) => {
      queryParams[key] = searchParams.get(key) || ""
    })

    if (pathParts[3] !== "template.html") {
      return undefined
    }

    delete queryParams["key"]

    return {
      host,
      paywallUIVariant: paywallUIVariant as "base" | "legacy",
      reviewRelease: reviewRelease as "release" | "review",
      version,
      base: base as "base2" | "base3",
      key,
      queryParams,
    }
  }
  private composeURL(parts: TemplateURLParts): string {
    let url = `${parts.host}/${parts.paywallUIVariant === "base" ? "base-" : ""}${
      parts.reviewRelease
    }/${parts.version}/${parts.base}/template.html?key=${parts.key}`

    let params = new URLSearchParams(parts.queryParams).toString()

    if (params) {
      url += `&${params}`
    }

    return url
  }

  public setKey(key: string): void {
    if (this.parts) {
      this.parts.key = key
    }
  }

  public async updateToLatestVersion(
    builderVersion: string | undefined = undefined
  ): Promise<void> {
    if (this.parts?.base === "base1") {
      return
    }

    // Upgrade to paywall-ui from paywall-next
    if (this.parts) {
      this.parts.paywallUIVariant = "base"
    } else {
      // We're not using a template url, so we can't upgrade
      // anyways
      return
    }

    const updateURL = `${
      process.env.NEXT_PUBLIC_PAYWALL_UI_HOST || "https://templates.superwall.com"
    }/${this.parts?.paywallUIVariant == "base" ? "base-" : ""}release/${
      builderVersion || "latest"
    }/manifest.json`

    const { version } = await fetch(updateURL).then((res) => res.json())

    if (this.parts) {
      this.parts!.version = version
    }
  }
}
